import React, { useState, useEffect } from "react";
import { db } from "../config/firebase";
import { getDocs, collection, query, where } from "firebase/firestore";
import ProfilePic from "./Default_pfp.svg.png";

export default function Sidebar({ onSelectStudent, refreshTrigger, teacherId, schoolId, classIds }) {
  const [studentList, setStudentList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGrade, setSelectedGrade] = useState("");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const MAX_NAME_LENGTH = 15;

  useEffect(() => {
    const getStudentList = async () => {
      try {
        let studentsQuery;
        
        if (classIds && classIds.length > 0) {
          // Filter students by teacher's classes
          studentsQuery = query(
            collection(db, "students"),
            where("school_id", "==", schoolId),
            where("class_id", "in", classIds)
          );
        } else {
          // Fallback to all students in the school
          studentsQuery = query(
            collection(db, "students"),
            where("school_id", "==", schoolId)
          );
        }

        const data = await getDocs(studentsQuery);
        const filteredData = data.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
          present: !doc.data().is_picked_up, // Map from new schema
        }));
        setStudentList(filteredData);
      } catch (err) {
        console.error(err);
      }
    };

    if (schoolId) {
      getStudentList();
    }
  }, [refreshTrigger, schoolId, classIds]);

  const filteredStudents = studentList
    .filter((student) => {
      const matchesSearchTerm = `${student.name} ${student.id}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesGrade =
        selectedGrade === "" || student.grade === selectedGrade;
      return matchesSearchTerm && matchesGrade;
    })
    .sort((a, b) => (a.present === b.present ? 0 : a.present ? -1 : 1));

  const truncateName = (name) =>
    name.length > MAX_NAME_LENGTH
      ? `${name.substring(0, MAX_NAME_LENGTH)}...`
      : name;

  // SafeLine Pickup Color Scheme
  const safeLineColors = {
    flameOrange: '#ff6b35',
    brightOrange: '#ff9500', 
    deepOrange: '#e55100',
    orangeTint: '#fff4f0',
    pureWhite: '#ffffff',
    lightGray: '#f8f9fa',
    coolGray: '#e9ecef',
    mediumGray: '#dee2e6',
    emerald: '#10b981',
    lightEmerald: '#d1fae5',
    forestGreen: '#047857',
    amber: '#f59e0b',
    lightAmber: '#fef3c7',
    darkAmber: '#d97706',
    red: '#ef4444',
    lightRed: '#fee2e2',
    darkRed: '#dc2626',
    darkCharcoal: '#1f2937',
    mediumCharcoal: '#374151',
    lightCharcoal: '#6b7280',
    mutedGray: '#9ca3af'
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div 
        className="p-4 border-b"
        style={{ 
          backgroundColor: safeLineColors.orangeTint,
          borderBottomColor: safeLineColors.coolGray
        }}
      >
        <h3 
          className="text-lg font-bold flex items-center"
          style={{ color: safeLineColors.darkCharcoal }}
        >
          <svg className="w-5 h-5 mr-2" style={{ color: safeLineColors.flameOrange }} fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
          </svg>
          Students
        </h3>
        <p 
          className="text-sm mt-1"
          style={{ color: safeLineColors.mediumCharcoal }}
        >
          {filteredStudents.length} student{filteredStudents.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Search Bar */}
      <div className="p-4">
        <div className="relative">
          <div 
            className="absolute left-3 top-1/2 transform -translate-y-1/2"
            style={{ color: safeLineColors.flameOrange }}
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
            </svg>
          </div>
          <input
            type="text"
            placeholder="Search by name or ID..."
            className="w-full pl-10 pr-4 py-3 rounded-lg border-2 transition-all duration-200 focus:outline-none focus:ring-2"
            style={{
              backgroundColor: safeLineColors.pureWhite,
              borderColor: safeLineColors.coolGray,
              color: safeLineColors.darkCharcoal
            }}
            onFocus={(e) => {
              e.target.style.borderColor = safeLineColors.flameOrange;
              e.target.style.boxShadow = `0 0 0 3px ${safeLineColors.orangeTint}`;
            }}
            onBlur={(e) => {
              e.target.style.borderColor = safeLineColors.coolGray;
              e.target.style.boxShadow = 'none';
            }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Filter */}
      <div className="px-4 pb-4">
        <select
          className="w-full px-3 py-2 rounded-lg border-2 transition-all duration-200 focus:outline-none focus:ring-2"
          style={{
            backgroundColor: safeLineColors.pureWhite,
            borderColor: safeLineColors.coolGray,
            color: safeLineColors.darkCharcoal
          }}
          onFocus={(e) => {
            e.target.style.borderColor = safeLineColors.flameOrange;
            e.target.style.boxShadow = `0 0 0 3px ${safeLineColors.orangeTint}`;
          }}
          onBlur={(e) => {
            e.target.style.borderColor = safeLineColors.coolGray;
            e.target.style.boxShadow = 'none';
          }}
          value={selectedGrade}
          onChange={(e) => setSelectedGrade(e.target.value)}
        >
          <option value="">All Grades</option>
          {Array.from({ length: 13 }, (_, i) =>
            i === 0 ? "K" : i.toString()
          ).map((grade) => (
            <option key={grade} value={grade}>
              Grade {grade}
            </option>
          ))}
        </select>
      </div>

      {/* Student List */}
      <div className="overflow-y-auto flex-1">
        {filteredStudents.length > 0 ? (
          filteredStudents.map((student) => (
            <div
              className="cursor-pointer mx-3 my-2 p-4 rounded-xl flex items-center gap-4 transition-all duration-200 border-2 hover:shadow-lg"
              style={{
                backgroundColor: student.present ? safeLineColors.pureWhite : safeLineColors.lightGray,
                borderColor: selectedStudent?.id === student.id ? safeLineColors.flameOrange : safeLineColors.coolGray,
                boxShadow: selectedStudent?.id === student.id ? `0 0 0 3px ${safeLineColors.orangeTint}` : 'none'
              }}
              key={student.id}
              onClick={() => {
                setSelectedStudent(student);
                onSelectStudent(student);
              }}
              onMouseEnter={(e) => {
                if (selectedStudent?.id !== student.id) {
                  e.target.style.borderColor = safeLineColors.flameOrange;
                  e.target.style.boxShadow = '0 2px 8px rgba(255, 107, 53, 0.15)';
                  e.target.style.transform = 'translateY(-1px)';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedStudent?.id !== student.id) {
                  e.target.style.borderColor = safeLineColors.coolGray;
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }
              }}
            >
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg"
                style={{ 
                  backgroundColor: student.present ? safeLineColors.orangeTint : safeLineColors.mediumGray,
                  color: student.present ? safeLineColors.flameOrange : safeLineColors.pureWhite
                }}
              >
                {student.name?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <div className="flex-1">
                <p 
                  className="font-semibold text-sm truncate"
                  style={{ color: safeLineColors.darkCharcoal }}
                >
                  {truncateName(student.name)}
                </p>
                <p 
                  className="text-xs mt-1"
                  style={{ color: safeLineColors.mediumCharcoal }}
                >
                  ID: {student.id}
                </p>
                <p 
                  className="text-xs"
                  style={{ color: safeLineColors.mediumCharcoal }}
                >
                  Grade: {student.grade}
                </p>
              </div>
              <div className="text-right text-xs">
                <div className="flex flex-col items-end gap-1">
                  <span 
                    className="px-2 py-1 rounded-full text-xs font-semibold"
                    style={{
                      backgroundColor: student.present ? safeLineColors.lightEmerald : safeLineColors.lightRed,
                      color: student.present ? safeLineColors.forestGreen : safeLineColors.darkRed
                    }}
                  >
                    {student.present ? "Present" : "Picked Up"}
                  </span>
                  {student.is_walking_today && (
                    <span 
                      className="px-2 py-1 rounded-full text-xs font-semibold"
                      style={{
                        backgroundColor: safeLineColors.lightAmber,
                        color: safeLineColors.darkAmber
                      }}
                    >
                      Walking
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div 
            className="m-4 p-6 text-center rounded-xl border-2 border-dashed"
            style={{ 
              backgroundColor: safeLineColors.orangeTint,
              borderColor: safeLineColors.coolGray
            }}
          >
            <svg 
              className="w-12 h-12 mx-auto mb-3" 
              style={{ color: safeLineColors.mutedGray }} 
              fill="currentColor" 
              viewBox="0 0 24 24"
            >
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
            <p 
              className="text-sm font-medium mb-1"
              style={{ color: safeLineColors.mediumCharcoal }}
            >
              No students found
            </p>
            <p 
              className="text-xs"
              style={{ color: safeLineColors.mutedGray }}
            >
              Try adjusting your search or filters
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
